// Settings (docs/cockpit/spec.md): drawn from the Parameters the Business Tree declares, each with its
// bounds. Changing one is a Tree Change: LAMP previews it in plain words with the open Runs it affects,
// and it applies only when an Owner confirms it. Staff changes go to the Owner as Suggestions. Locked
// statutory Parameters are shown, never editable.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  ApiError,
  type Api,
  type ParameterValue,
  type Settings as SettingsData,
  type SettingsParameter,
  type TreeChange,
} from "../api.js";
import { CATALOGUES, useWords, type Words } from "../i18n/words.js";
import { ChangePreview, appliedNotice } from "./ChangePreview.js";
import { RingSetting } from "./RingSetting.js";
import { SupplierParticulars } from "./SupplierParticulars.js";

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.settingsRefused(error.code)
    : w.settingsUnreachable;

const keyOf = (p: SettingsParameter) => `${p.node}.${p.name}`;
const shown = (value: ParameterValue, w: Words) =>
  typeof value === "boolean" ? (value ? w.settingsYes : w.settingsNo) : String(value);

/** The typed value of a Parameter's input, or why it is not one (in `words`' language). */
export function parseSetting(
  p: SettingsParameter,
  text: string,
  words: Words = CATALOGUES.en,
): { value: ParameterValue } | { problem: string } {
  switch (p.type) {
    case "bool":
      return { value: text === "true" };
    case "enum":
    case "string":
      return { value: text };
    case "int":
    case "number": {
      const value = Number(text);
      if (text.trim() === "" || !Number.isFinite(value)) return { problem: words.settingsEnterNumber };
      if (p.type === "int" && !Number.isInteger(value)) return { problem: words.settingsEnterWholeNumber };
      if (p.min !== null && value < p.min) return { problem: words.settingsLeast(p.min) };
      if (p.max !== null && value > p.max) return { problem: words.settingsMost(p.max) };
      return { value };
    }
  }
}

function Bounds({ p }: { p: SettingsParameter }) {
  const w = useWords();
  const bounds = [
    p.min !== null ? w.settingsAtLeast(p.min) : null,
    p.max !== null ? w.settingsAtMost(p.max) : null,
  ].filter((x): x is string => x !== null);
  return (
    <span className="small muted">
      {[...bounds, w.settingsDefault].join(", ")}{" "}
      {/* An enum's or a text's default is the pack's own value (as in rent=0, sunday), which #220 translates. */}
      {typeof p.default === "string" ? <span data-pack="">{p.default}</span> : shown(p.default, w)}
    </span>
  );
}

function ParameterRow({
  p,
  busy,
  onPreview,
}: {
  p: SettingsParameter;
  busy: boolean;
  onPreview: (p: SettingsParameter, text: string) => void;
}) {
  const w = useWords();
  const [text, setText] = useState(String(p.value));
  // Reset the input only when the Parameter's value changes (say, after a confirmed change). An effect
  // would also fire after mount and could overwrite what the person has already started typing.
  const [valueShown, setValueShown] = useState(p.value);
  if (valueShown !== p.value) {
    setValueShown(p.value);
    setText(String(p.value));
  }
  const id = `setting-${keyOf(p).replaceAll(".", "-")}`;
  const label = p.description ?? p.name.replaceAll("_", " ");
  const locked = p.lockedBy !== null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onPreview(p, text);
  };
  return (
    <li className="item">
      <form onSubmit={submit} noValidate>
        {/* The pack's own words, which the packs translate (#220): marked so the Hindi evals know them. */}
        <label className="field" htmlFor={id} data-pack="">
          {label}
        </label>
        {p.type === "bool" ? (
          <select id={id} value={text} disabled={locked} onChange={(e) => setText(e.target.value)}>
            <option value="true">{w.settingsYes}</option>
            <option value="false">{w.settingsNo}</option>
          </select>
        ) : p.type === "enum" ? (
          <select id={id} value={text} disabled={locked} onChange={(e) => setText(e.target.value)}>
            {(p.values ?? []).map((v) => (
              <option key={v} value={v} data-pack="">
                {v}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={id}
            type={p.type === "string" ? "text" : "number"}
            inputMode={p.type === "int" ? "numeric" : p.type === "number" ? "decimal" : undefined}
            min={p.min ?? undefined}
            max={p.max ?? undefined}
            step={p.type === "int" ? 1 : "any"}
            value={text}
            disabled={locked}
            onChange={(e) => setText(e.target.value)}
          />
        )}{" "}
        {locked ? (
          <span className="tag tag-plain">{w.settingsLockedBy(p.lockedBy ?? "")}</span>
        ) : (
          <button type="submit" className="btn" disabled={busy || text === String(p.value)}>
            {w.settingsPreviewChange}
          </button>
        )}
        <br />
        <Bounds p={p} />
      </form>
    </li>
  );
}

export function Settings({ api, owner }: { api: Api; owner: boolean }) {
  const w = useWords();
  const [settings, setSettings] = useState<SettingsData | undefined>(undefined);
  const [pending, setPending] = useState<readonly TreeChange[]>([]);
  const [preview, setPreview] = useState<TreeChange | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.settings().then(setSettings, (error: unknown) => setProblem(problemOf(error, w)));
    if (owner) api.pendingChanges().then(setPending, () => setPending([]));
  }, [api, owner, w]);
  useEffect(load, [load]);

  const propose = async (p: SettingsParameter, text: string) => {
    setNotice(undefined);
    setProblem(undefined);
    const parsed = parseSetting(p, text, w);
    if ("problem" in parsed) {
      setProblem(parsed.problem);
      return;
    }
    setBusy(true);
    try {
      setPreview(await api.proposeSetting(p.node, p.name, parsed.value));
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  /** The Owner agreed to the Credits shown: the change comes back with what the agent's new answers found. */
  const testWithAgent = async (change: TreeChange, milliCredits: number) => {
    setBusy(true);
    setProblem(undefined);
    try {
      const tested = await api.testChangeWithAgent(change.id, milliCredits);
      setPreview((shown) => (shown?.id === tested.id ? tested : shown));
      setPending((kept) => kept.map((c) => (c.id === tested.id ? tested : c)));
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (change: TreeChange, migrate: boolean) => {
    setBusy(true);
    setProblem(undefined);
    try {
      const applied = await api.confirmChange(change.id, migrate);
      setPreview(undefined);
      setNotice(appliedNotice(applied, w));
      load();
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.settingsLoading}</p>;
  const nodes = [...new Set(settings.parameters.map((p) => p.node))];
  const waiting = pending.filter((c) => c.id !== preview?.id);
  return (
    <>
      <p className="muted">{w.settingsTreeVersion(settings.version)}</p>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      {preview ? (
        <ChangePreview
          change={preview}
          owner={owner}
          busy={busy}
          onConfirm={(c, m) => void confirm(c, m)}
          onDismiss={() => setPreview(undefined)}
          onTestWithAgent={(c, credits) => void testWithAgent(c, credits)}
        />
      ) : null}
      {waiting.length > 0 ? (
        <section className="section" aria-labelledby="waiting-heading">
          <h2 id="waiting-heading">{w.settingsWaiting}</h2>
          {waiting.map((c) => (
            <ChangePreview
              key={c.id}
              change={c}
              owner={owner}
              busy={busy}
              onConfirm={(x, m) => void confirm(x, m)}
              onTestWithAgent={(x, credits) => void testWithAgent(x, credits)}
            />
          ))}
        </section>
      ) : null}
      {nodes.length === 0 ? <p className="muted">{w.settingsNothing}</p> : null}
      {nodes.map((node) => {
        const params = settings.parameters.filter((p) => p.node === node);
        const headingId = `settings-${node.replaceAll(".", "-")}`;
        return (
          <section key={node} className="section" aria-labelledby={headingId}>
            <h2 id={headingId} data-pack="">
              {params[0]?.nodeName ?? node}
            </h2>
            <ul className="plain-list">
              {params.map((p) => (
                <ParameterRow key={keyOf(p)} p={p} busy={busy} onPreview={(x, t) => void propose(x, t)} />
              ))}
            </ul>
          </section>
        );
      })}
      {owner ? <SupplierParticulars api={api} /> : null}
      {owner ? <RingSetting api={api} /> : null}
    </>
  );
}
